import { useState } from "react";
import PlaceAutocomplete from "../components/PlaceAutocomplete";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plane, Building2, ArrowRight, Star, Search, Clock, Check } from "lucide-react";
import SaveTripDialog from "../components/SaveTripDialog";
import AddToScenarioDialog from "../components/planner/AddToScenarioDialog";

function fmtDuration(iso) {
  if (!iso) return "";
  const m = iso.match(/P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?/);
  if (!m) return iso;
  const hours = (parseInt(m[1] || 0, 10) * 24) + parseInt(m[2] || 0, 10);
  return [hours ? `${hours}h` : null, m[3] ? `${m[3]}m` : null].filter(Boolean).join(" ") || iso;
}

function fmtTime(iso) {
  if (!iso) return "—";
  return iso.slice(11, 16);
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso.slice(0, 10) + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function fmtMoney(amount, currency) {
  if (!amount) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 }).format(parseFloat(amount));
  } catch {
    return `${amount} ${currency || ""}`;
  }
}

function SliceRow({ slice }) {
  return (
    <div className="flex items-center gap-4 py-2">
      <div className="text-right w-20">
        <p className="font-display font-medium text-lg leading-tight">{fmtTime(slice.departing_at)}</p>
        <p className="font-mono text-xs text-sand-500">{slice.origin}</p>
      </div>
      <div className="flex-1 flex flex-col items-center">
        <p className="text-[10px] text-sand-400 font-mono mb-0.5">{fmtDuration(slice.duration)}</p>
        <div className="w-full flex items-center gap-1">
          <div className="h-px bg-sand-300 flex-1" />
          <Plane size={12} className="text-sand-400 rotate-45" />
          <div className="h-px bg-sand-300 flex-1" />
        </div>
        <p className="text-[10px] font-mono mt-0.5 text-sand-500">
          {slice.stops === 0 ? "Nonstop" : `${slice.stops} stop${slice.stops > 1 ? "s" : ""}${slice.segments.length > 1 ? " · " + slice.segments.slice(0, -1).map((s) => s.destination).join(", ") : ""}`}
        </p>
      </div>
      <div className="w-20">
        <p className="font-display font-medium text-lg leading-tight">{fmtTime(slice.arriving_at)}</p>
        <p className="font-mono text-xs text-sand-500">{slice.destination}</p>
      </div>
    </div>
  );
}

function OfferCard({ offer }) {
  const isGoogle = offer.source === "google";
  const [saveDialog, setSaveDialog] = useState(false);
  const [scenarioDialog, setScenarioDialog] = useState(false);
  const [saved, setSaved] = useState(false);
  return (
    <div className="bg-surface rounded-card border border-hairline p-5 hover:border-ember transition-colors" data-testid={`offer-card-${offer.id}`}>
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-center gap-3 md:w-44 shrink-0">
          {offer.airline_logo ? (
            <img src={offer.airline_logo} alt={offer.airline} className="w-8 h-8 object-contain" />
          ) : (
            <div className="w-8 h-8 bg-sand-100 flex items-center justify-center font-mono text-xs">{offer.airline_iata}</div>
          )}
          <div>
            <p className="text-sm font-medium leading-tight">{offer.airline}</p>
            <p className="text-[10px] text-sand-500 uppercase tracking-wide">{offer.cabin_class?.replace("_", " ")}</p>
            {isGoogle ? (
              <span className="inline-block mt-1 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-sand-300 text-sand-500" data-testid="offer-source-google">
                via Google
              </span>
            ) : (
              <span className="inline-block mt-1 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#DFE9DC] text-success border border-[#C9DCC7]" data-testid="offer-source-duffel">
                Bookable
              </span>
            )}
          </div>
        </div>
        <div className="flex-1 divide-y divide-sand-100">
          {offer.slices.map((s, i) => <SliceRow key={i} slice={s} />)}
        </div>
        <div className="md:w-40 md:text-right shrink-0 flex md:block items-start justify-between border-t md:border-t-0 border-sand-100 pt-3 md:pt-0">
          <div>
            <p className="font-display font-medium text-2xl tracking-tight" data-testid="offer-price">
              {fmtMoney(offer.total_amount, offer.total_currency)}
            </p>
            <p className="text-[10px] text-sand-500">
              {isGoogle && offer.round_trip_outbound_only ? "round-trip · outbound shown" : "total, all travelers"}
            </p>
          </div>
          <div className="flex flex-col items-end md:items-end gap-1 md:mt-2">
            {saved ? (
              <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-emerald-700" data-testid="offer-saved-label">
                <Check size={10} /> Saved
              </span>
            ) : (
              <button onClick={() => setSaveDialog(true)}
                className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ember hover:underline"
                data-testid="offer-save-trip-btn">
                + Save to trip
              </button>
            )}
            <button onClick={() => setScenarioDialog(true)}
              className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ember hover:underline"
              data-testid="offer-add-scenario-btn">
              + Add to scenario
            </button>
            {isGoogle && offer.booking_url && (
              <a href={offer.booking_url} target="_blank" rel="noreferrer"
                className="font-mono text-[10px] font-semibold uppercase tracking-wider text-sand-500 hover:underline"
                data-testid="offer-google-link">
                Book on Google ↗
              </a>
            )}
          </div>
        </div>
      </div>
      {saveDialog && <SaveTripDialog offer={offer} onClose={(ok) => { setSaveDialog(false); if (ok) setSaved(true); }} />}
      {scenarioDialog && <AddToScenarioDialog offer={offer} onClose={() => setScenarioDialog(false)} />}
    </div>
  );
}

function StayCard({ stay, nights }) {
  const perNight = stay.total_amount && nights > 0 ? parseFloat(stay.total_amount) / nights : null;
  return (
    <div className="bg-surface rounded-card border border-hairline overflow-hidden hover:border-ember transition-colors group" data-testid={`stay-card-${stay.id}`}>
      <div className="h-40 bg-sand-100 relative overflow-hidden">
        {stay.photo ? (
          <img src={stay.photo} alt={stay.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sand-300"><Building2 size={32} /></div>
        )}
        {stay.rating ? (
          <span className="absolute top-2 left-2 bg-surface/90 backdrop-blur px-2 py-0.5 text-xs font-mono flex items-center gap-1">
            <Star size={10} className="fill-amber-400 text-amber-400" /> {stay.rating}
          </span>
        ) : null}
      </div>
      <div className="p-4">
        <h3 className="font-display font-medium text-base leading-snug mb-1 line-clamp-2">{stay.name}</h3>
        <p className="text-xs text-sand-500 mb-3 line-clamp-1">{[stay.address, stay.city].filter(Boolean).join(", ")}</p>
        <div className="flex items-end justify-between pt-3 border-t border-sand-100">
          <div>
            {stay.review_score ? (
              <p className="text-xs text-sand-600"><span className="font-mono font-bold text-ember">{stay.review_score}</span>{stay.review_count ? ` · ${stay.review_count} reviews` : ""}</p>
            ) : <span />}
            {stay.link && (
              <a href={stay.link} target="_blank" rel="noreferrer"
                className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ember hover:underline"
                data-testid="stay-link">
                View ↗
              </a>
            )}
          </div>
          <div className="text-right">
            <p className="font-display font-medium text-xl tracking-tight" data-testid="stay-price">{fmtMoney(stay.total_amount, stay.currency)}</p>
            <p className="text-[10px] text-sand-500">{perNight ? `${fmtMoney(String(perNight), stay.currency)}/night · ` : ""}{nights} night{nights !== 1 ? "s" : ""}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FlightsTab() {
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [departDate, setDepartDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [adults, setAdults] = useState(1);
  const [cabin, setCabin] = useState("economy");
  const [nonstop, setNonstop] = useState(false);
  const [offers, setOffers] = useState(null);
  const [airlines, setAirlines] = useState(null);
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!origin?.iata_code || !destination?.iata_code) { toast.error("Pick an origin and destination from the suggestions"); return; }
    if (!departDate) { toast.error("Pick a departure date"); return; }
    setLoading(true);
    setOffers(null);
    setAirlines(null);
    setSources(null);
    try {
      const { data } = await api.post("/planning/flights/search", {
        origin: origin.iata_code,
        destination: destination.iata_code,
        departure_date: departDate,
        return_date: returnDate || null,
        adults,
        cabin_class: cabin,
        max_connections: nonstop ? 0 : 1,
      });
      setOffers(data.offers);
      setAirlines(data.airlines || []);
      setSources(data.sources || null);
      if (data.offers.length === 0) toast.info("No flights found for this route and dates");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <div className="bg-surface rounded-card border border-hairline p-6 md:p-8 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <PlaceAutocomplete label="From" placeholder="City or airport" value={origin} onSelect={setOrigin} testId="flight-origin-input" />
          <PlaceAutocomplete label="To" placeholder="City or airport" value={destination} onSelect={setDestination} testId="flight-destination-input" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-5">
          <div>
            <label className="label-tag block mb-2">Depart</label>
            <input type="date" min={today} value={departDate} onChange={(e) => setDepartDate(e.target.value)}
              className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember"
              data-testid="flight-depart-date" />
          </div>
          <div>
            <label className="label-tag block mb-2">Return <span className="text-sand-400 normal-case">(optional)</span></label>
            <input type="date" min={departDate || today} value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
              className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember"
              data-testid="flight-return-date" />
          </div>
          <div>
            <label className="label-tag block mb-2">Travelers</label>
            <select value={adults} onChange={(e) => setAdults(parseInt(e.target.value, 10))}
              className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember"
              data-testid="flight-adults-select">
              {[1,2,3,4,5,6].map((n) => <option key={n} value={n}>{`${n} adult${n > 1 ? "s" : ""}`}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tag block mb-2">Cabin</label>
            <select value={cabin} onChange={(e) => setCabin(e.target.value)}
              className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember"
              data-testid="flight-cabin-select">
              <option value="economy">Economy</option>
              <option value="premium_economy">Premium Economy</option>
              <option value="business">Business</option>
              <option value="first">First</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-sand-700 cursor-pointer">
            <input type="checkbox" checked={nonstop} onChange={(e) => setNonstop(e.target.checked)} className="accent-ember" data-testid="flight-nonstop-checkbox" />
            Nonstop only
          </label>
          <button onClick={search} disabled={loading} className="btn-swiss btn-primary text-sm disabled:opacity-50" data-testid="flight-search-btn">
            <Search size={16} className="mr-2" /> {loading ? "Searching airlines…" : "Search flights"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="space-y-4" data-testid="flight-loading">
          {[0,1,2].map((i) => <div key={i} className="rounded-card border border-hairline bg-surface h-28 animate-pulse" />)}
          <p className="text-center text-sm text-sand-500 flex items-center justify-center gap-2"><Clock size={14} /> Querying live airline inventory — this can take up to 35 seconds</p>
        </div>
      )}

      {offers && offers.length > 0 && (
        <div data-testid="flight-results">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
            <p className="label-tag">{offers.length} offers · sorted by price</p>
            <div className="flex items-baseline gap-4">
              {sources && (
                <p className="font-mono text-[11px] text-sand-400" data-testid="source-coverage">
                  Duffel {sources.duffel} · Google {sources.google}
                </p>
              )}
              {airlines && airlines.length > 0 && (
                <p className="font-mono text-[11px] text-sand-500" data-testid="carrier-coverage">
                  Carriers: {airlines.map((a) => `${a.iata || a.name} (${a.count})`).join(" · ")}
                </p>
              )}
            </div>
          </div>
          {sources && sources.google === 0 && (
            <div className="border border-[#E9D5AE] bg-[#F3E7D3] px-4 py-3 mb-4 text-xs text-[#8A5F22]" data-testid="single-carrier-notice">
              Google Flights coverage was unavailable for this search — results are limited to carriers activated
              on your Duffel account.
            </div>
          )}
          <div className="space-y-3">
            {offers.map((o) => <OfferCard key={o.id} offer={o} />)}
          </div>
        </div>
      )}

      {offers && offers.length === 0 && (
        <div className="rounded-card border border-hairline bg-surface p-10 text-center text-sand-600 text-sm" data-testid="flight-empty">
          No flights found. Try different dates or allow connections.
        </div>
      )}
    </div>
  );
}

function StaysTab() {
  const [place, setPlace] = useState(null);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState(2);
  const [rooms, setRooms] = useState(1);
  const [results, setResults] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [notEnabled, setNotEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const nights = checkIn && checkOut
    ? Math.max(Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000), 0)
    : 0;

  const search = async () => {
    if (!place?.latitude || !place?.longitude) { toast.error("Pick a destination from the suggestions"); return; }
    if (!checkIn || !checkOut) { toast.error("Pick check-in and check-out dates"); return; }
    if (checkOut <= checkIn) { toast.error("Check-out must be after check-in"); return; }
    setLoading(true);
    setResults(null);
    setNotEnabled(false);
    try {
      const { data } = await api.post("/planning/stays/search", {
        location: place.city_name || place.name,
        latitude: place.latitude,
        longitude: place.longitude,
        radius_km: 5,
        check_in_date: checkIn,
        check_out_date: checkOut,
        guests,
        rooms,
      });
      if (data.not_enabled) {
        setNotEnabled(true);
        return;
      }
      setResults(data.results);
      setTestMode(!!data.test_mode);
      if (data.results.length === 0) toast.info("No stays found for this location and dates");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <div className="bg-surface rounded-card border border-hairline p-6 md:p-8 mb-8">
        <div className="mb-5">
          <PlaceAutocomplete label="Destination" placeholder="City" value={place} onSelect={setPlace} testId="stays-destination-input" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-5">
          <div>
            <label className="label-tag block mb-2">Check-in</label>
            <input type="date" min={today} value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
              className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember"
              data-testid="stays-checkin" />
          </div>
          <div>
            <label className="label-tag block mb-2">Check-out</label>
            <input type="date" min={checkIn || today} value={checkOut} onChange={(e) => setCheckOut(e.target.value)}
              className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember"
              data-testid="stays-checkout" />
          </div>
          <div>
            <label className="label-tag block mb-2">Guests</label>
            <select value={guests} onChange={(e) => setGuests(parseInt(e.target.value, 10))}
              className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember"
              data-testid="stays-guests-select">
              {[1,2,3,4,5,6].map((n) => <option key={n} value={n}>{`${n} guest${n > 1 ? "s" : ""}`}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tag block mb-2">Rooms</label>
            <select value={rooms} onChange={(e) => setRooms(parseInt(e.target.value, 10))}
              className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember"
              data-testid="stays-rooms-select">
              {[1,2,3,4].map((n) => <option key={n} value={n}>{`${n} room${n > 1 ? "s" : ""}`}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end">
          <button onClick={search} disabled={loading} className="btn-swiss btn-primary text-sm disabled:opacity-50" data-testid="stays-search-btn">
            <Search size={16} className="mr-2" /> {loading ? "Searching stays…" : "Search stays"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stays-loading">
          {[0,1,2].map((i) => <div key={i} className="rounded-card border border-hairline bg-surface h-64 animate-pulse" />)}
        </div>
      )}

      {notEnabled && (
        <div className="border border-amber-300 bg-amber-50 p-8 md:p-10" data-testid="stays-not-enabled">
          <p className="label-tag mb-3 text-amber-700">Hotel search pending</p>
          <h3 className="font-display font-medium text-xl mb-2">Duffel Stays isn't enabled on your account yet.</h3>
          <p className="text-sm text-sand-700 leading-relaxed max-w-xl">
            Duffel gates hotel inventory behind an access request. Ask them to enable Stays for your
            organisation at{" "}
            <a href="https://duffel.com/contact-us" target="_blank" rel="noreferrer" className="underline text-ember">
              duffel.com/contact-us
            </a>
            . Flight search works now — hotel search will light up here automatically once access is granted.
          </p>
        </div>
      )}

      {results && results.length > 0 && (
        <div data-testid="stays-results">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <p className="label-tag">{results.length} stays · sorted by price</p>
            {results[0]?.source === "google" && (
              <span className="text-[10px] font-mono uppercase tracking-wide border border-sand-300 text-sand-500 px-2 py-0.5" data-testid="stays-google-badge">
                via Google Hotels
              </span>
            )}
            {testMode && (
              <span className="text-[10px] font-mono uppercase tracking-wide bg-amber-100 text-amber-800 px-2 py-0.5" data-testid="stays-test-mode-badge">
                Sample data — live hotel inventory pending Duffel access
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((s) => <StayCard key={s.id} stay={s} nights={nights} />)}
          </div>
        </div>
      )}

      {results && results.length === 0 && (
        <div className="rounded-card border border-hairline bg-surface p-10 text-center text-sand-600 text-sm" data-testid="stays-empty">
          No stays found. Try a bigger city or different dates.
        </div>
      )}
    </div>
  );
}

export default function Plan() {
  const [tab, setTab] = useState("flights");

  return (
    <div>
      <main className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-16" data-testid="plan-page">
        <div className="mb-10">
          <p className="label-tag mb-3">Plan</p>
          <h1 className="heading-h1 text-4xl sm:text-5xl lg:text-6xl mb-2">Where to next?</h1>
          <p className="text-sand-600 text-base mt-3">Search live flights and hotels — Duffel + Google, merged.</p>
          <div className="divider-strong mt-4" />
        </div>

        <div className="flex gap-2 mb-8">
          <button
            onClick={() => setTab("flights")}
            className={`btn-swiss text-sm ${tab === "flights" ? "btn-primary" : "btn-outline"}`}
            data-testid="plan-tab-flights"
          >
            <Plane size={16} className="mr-2" /> Flights
          </button>
          <button
            onClick={() => setTab("stays")}
            className={`btn-swiss text-sm ${tab === "stays" ? "btn-primary" : "btn-outline"}`}
            data-testid="plan-tab-stays"
          >
            <Building2 size={16} className="mr-2" /> Hotels
          </button>
        </div>

        {tab === "flights" ? <FlightsTab /> : <StaysTab />}
      </main>
    </div>
  );
}
